import React from 'react';
import { useTranslation } from 'react-i18next';
import { TableHead, TableRow, TableCell, Tooltip, Stack, Typography, CircularProgress, Box, Checkbox } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

/**
 * DocumentTableHeader - Table header row with column names
 *
 * Pure presentational component for the table header.
 * Displays column names for: Title, Visibility, Owner, Shared Groups, Shared Users, Source, Actions
 */
export interface DocumentTableHeaderProps {
  /** Present when bulk selection is on: state of the page's selectable rows. */
  selection?: { selectableCount: number; selectedCount: number; onToggleAll: (select: boolean) => void };
}

export const DocumentTableHeader: React.FC<DocumentTableHeaderProps> = ({ selection }) => {
  const { t } = useTranslation();
  const iconSx = { fontSize: 18 } as const;
  const legend = (
    <Stack spacing={0.75} sx={{ p: 0.5 }}>
      <Typography variant="subtitle2" component="div">{t('documents.table.status.legendTitle')}</Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <CheckCircleOutlineIcon sx={{ ...iconSx, color: 'success.light' }} />
        <Typography variant="body2">{t('documents.table.status.completed')}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box sx={{ display: 'inline-flex', width: 18, justifyContent: 'center' }}><CircularProgress size={12} thickness={5} color="inherit" /></Box>
        <Typography variant="body2">{t('documents.table.status.processing')}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <HourglassEmptyOutlinedIcon sx={iconSx} />
        <Typography variant="body2">{t('documents.table.status.pending')}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <ErrorOutlineIcon sx={{ ...iconSx, color: 'error.light' }} />
        <Typography variant="body2">{t('documents.table.status.failed')}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <WarningAmberOutlinedIcon sx={{ ...iconSx, color: 'warning.light' }} />
        <Typography variant="body2">{t('documents.table.ocrReview')}</Typography>
      </Stack>
    </Stack>
  );

  return (
    <TableHead>
      <TableRow>
        {selection && (
          <TableCell padding="checkbox" sx={{ width: 48 }}>
            <Checkbox
              size="small"
              checked={selection.selectableCount > 0 && selection.selectedCount === selection.selectableCount}
              indeterminate={selection.selectedCount > 0 && selection.selectedCount < selection.selectableCount}
              disabled={selection.selectableCount === 0}
              onChange={(e) => selection.onToggleAll(e.target.checked)}
              aria-label={t('documents.bulk.selectAll')}
            />
          </TableCell>
        )}
        {/* Fixed layout: these widths size every column, the title takes the rest. */}
        <TableCell>{t('documents.table.title')}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', px: 1, width: 84 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <span>{t('documents.table.status.header')}</span>
            <Tooltip arrow title={legend} slotProps={{ tooltip: { sx: { maxWidth: 260 } } }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} aria-label={t('documents.table.status.legendTitle')} />
            </Tooltip>
          </Stack>
        </TableCell>
        <TableCell sx={{ width: 140 }}>{t('documents.table.visibility')}</TableCell>
        <TableCell sx={{ width: 170 }}>{t('documents.table.owner')}</TableCell>
        <TableCell sx={{ width: 110 }}>{t('documents.table.sharedGroups')}</TableCell>
        <TableCell sx={{ width: 110 }}>{t('documents.table.sharedUsers')}</TableCell>
        <TableCell sx={{ width: 150 }}>{t('documents.table.sourceType')}</TableCell>
        <TableCell align="right" sx={{ width: 196 }}>{t('documents.table.actions')}</TableCell>
      </TableRow>
    </TableHead>
  );
};

DocumentTableHeader.displayName = 'DocumentTableHeader';
